import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Grid, Typography, Stack, CircularProgress, Alert, Skeleton,
  Tooltip, Chip,
} from '@mui/material';
import {
  Speed, TrendingUp, Bolt, Layers, BarChart as BarChartIcon,
  HelpOutline, ShoppingCart, Warning,
} from '@mui/icons-material';
import { api } from '../config/api';

interface StatsData {
  periodo: { dias: number };
  velocidad: { segundos: number; meta: number };
  leads: { bajo: number; medio: number; alto: number; promedio: number };
  conversion: { porcentaje: number };
  autonomia: { bot: number; agente: number };
  canales: Record<string, number>;
  integraciones: Record<string, any>;
  ahorroEstimado: number;
}

const MetricaCard: React.FC<{
  icon: React.ReactNode;
  titulo: string;
  valor: string | number;
  unidad?: string;
  color?: string;
  help?: string;
  subtitulo?: string;
}> = ({ icon, titulo, valor, unidad, color = '#4285F4', help, subtitulo }) => (
  <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
    <CardContent>
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ color, fontSize: 24 }}>{icon}</Box>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                {titulo}
              </Typography>
              {help && (
                <Tooltip title={help} arrow>
                  <HelpOutline sx={{ fontSize: 14, cursor: 'help', color: '#999' }} />
                </Tooltip>
              )}
            </Stack>
          </Box>
        </Stack>
        <Typography variant="h5" sx={{ fontWeight: 700, color }}>
          {valor}{unidad ? ` ${unidad}` : ''}
        </Typography>
        {subtitulo && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
            {subtitulo}
          </Typography>
        )}
      </Stack>
    </CardContent>
  </Card>
);

const StatsIntegraciones: React.FC = () => {
  const [stats, setStats] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const cargar = async () => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await api.get('/dashboard/integraciones-stats');
        setStats(data);
      } catch (e: any) {
        setError(e.response?.data?.error || 'Error cargando estadísticas');
        setStats(null);
      } finally {
        setLoading(false);
      }
    };
    cargar();
  }, []);

  if (loading) {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Resultados Medibles</Typography>
        <Grid container spacing={2}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 1 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error || !stats) {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Resultados Medibles</Typography>
        <Alert severity="info" icon={<Warning />}>
          {error || 'Sin datos aún. Espera a que tu agente procese algunos mensajes.'}
        </Alert>
      </Box>
    );
  }

  const velocidadRapida = stats.velocidad.segundos < stats.velocidad.meta;
  const tiempoAhorro = Math.max(0, 300 - Math.round(stats.velocidad.segundos)); // 5 min - tiempo actual

  return (
    <Box sx={{ mb: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <BarChartIcon sx={{ fontSize: 20 }} />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Resultados Medibles — Últimos {stats.periodo.dias} días
        </Typography>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Velocidad de Respuesta */}
        <Grid item xs={12} sm={6} md={4}>
          <MetricaCard
            icon={<Speed />}
            titulo="Velocidad de Atención"
            valor={stats.velocidad.segundos}
            unidad="segundos"
            color={velocidadRapida ? '#10b981' : '#f59e0b'}
            help="Promedio de tiempo que tarda el bot en responder desde que el cliente escribe"
            subtitulo={`Meta: ${stats.velocidad.meta}s | Ahorro: ~${tiempoAhorro}s/respuesta vs respuesta manual`}
          />
        </Grid>

        {/* Generación de Leads */}
        <Grid item xs={12} sm={6} md={4}>
          <MetricaCard
            icon={<TrendingUp />}
            titulo="Tasa de Conversión"
            valor={`${stats.conversion.porcentaje}%`}
            color="#8b5cf6"
            help="% de conversaciones que generan un lead interesado"
            subtitulo={`${stats.leads.alto} leads high-intent (score >70)`}
          />
        </Grid>

        {/* Autonomía */}
        <Grid item xs={12} sm={6} md={4}>
          <MetricaCard
            icon={<Bolt />}
            titulo="Autonomía del Bot"
            valor={`${stats.autonomia.bot}%`}
            color="#f59e0b"
            help="% de mensajes que el bot responde automáticamente vs requieren un humano"
            subtitulo={`${stats.autonomia.agente}% requirió intervención humana`}
          />
        </Grid>

        {/* Leads por Score */}
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
            <CardContent>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ color: '#ec4899', fontSize: 24 }}><TrendingUp /></Box>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                      Leads Capturados
                    </Typography>
                    <Tooltip title="Clasificación por nivel de interés: bajo (<40), medio (40-70), alto (>70)" arrow>
                      <HelpOutline sx={{ fontSize: 14, cursor: 'help', color: '#999' }} />
                    </Tooltip>
                  </Stack>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Chip label={`Alto: ${stats.leads.alto}`} color="success" variant="outlined" size="small" />
                  <Chip label={`Medio: ${stats.leads.medio}`} color="warning" variant="outlined" size="small" />
                  <Chip label={`Bajo: ${stats.leads.bajo}`} color="default" variant="outlined" size="small" />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Score promedio: {stats.leads.promedio.toFixed(1)}/100
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Tráfico por Canal */}
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
            <CardContent>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ color: '#06b6d4', fontSize: 24 }}><Layers /></Box>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                      Tráfico por Canal
                    </Typography>
                    <Tooltip title="Distribución de mensajes por origen: WhatsApp, Instagram, Messenger, Widget" arrow>
                      <HelpOutline sx={{ fontSize: 14, cursor: 'help', color: '#999' }} />
                    </Tooltip>
                  </Stack>
                </Stack>
                <Stack spacing={0.5} sx={{ fontSize: 12 }}>
                  {Object.entries(stats.canales).map(([canal, count]) => (
                    count > 0 && (
                      <Typography key={canal} variant="body2" color="text.secondary">
                        <strong>{canal.charAt(0).toUpperCase() + canal.slice(1)}</strong>: {count} msgs
                      </Typography>
                    )
                  ))}
                  {Object.values(stats.canales).every((v) => v === 0) && (
                    <Typography variant="body2" color="text.secondary">
                      Sin tráfico aún
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Ahorro Estimado */}
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
            <CardContent>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ color: '#fff', fontSize: 24 }}><ShoppingCart /></Box>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="caption" color="#fff" sx={{ fontSize: 11 }}>
                      Ahorro Mensual
                    </Typography>
                    <Tooltip title="Estimado en base a leads de alta intención que evitan costo de soporte manual" arrow>
                      <HelpOutline sx={{ fontSize: 14, cursor: 'help', color: '#fff' }} />
                    </Tooltip>
                  </Stack>
                </Stack>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff' }}>
                  ${stats.ahorroEstimado.toLocaleString('es-CL')}
                </Typography>
                <Typography variant="caption" color="#e0f2fe" sx={{ fontSize: 10 }}>
                  En costos de soporte evitados
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Integraciones Activas */}
      {(stats.integraciones.mercadolibre?.conectada || stats.integraciones.woocommerce?.conectada) && (
        <Card sx={{ background: '#f0f9ff', border: '1px solid #bfdbfe' }}>
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Warning sx={{ fontSize: 18, color: '#0284c7' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0284c7' }}>
                Integraciones Ecommerce Activas
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Tu agente está consultando en tiempo real:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {stats.integraciones.mercadolibre?.conectada && (
                <Chip
                  icon={<TrendingUp />}
                  label="🏪 Mercado Libre: productos, pedidos, preguntas automáticas"
                  variant="outlined"
                  color="primary"
                  size="small"
                />
              )}
              {stats.integraciones.woocommerce?.conectada && (
                <Chip
                  icon={<ShoppingCart />}
                  label="🛒 WooCommerce: catálogo y estado de pedidos"
                  variant="outlined"
                  color="success"
                  size="small"
                />
              )}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default StatsIntegraciones;
